// src/pages/ChoosePlan.js
//
// Step 3 of 4. Reads the role/state/grade captured at registration, shows only
// that role's plans, collects a seat count where it applies, and hands off to
// the createCheckoutSession callable.
//
// The callable is what makes this different from the old buy.stripe.com links:
// it attaches client_reference_id and firebaseUid metadata, which is the only
// reason the webhook can connect a payment back to an account.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db, functions } from "../firebase/firebaseConfig";
import {
  plansForRoleAndGrade,
  PLANS,
  DISTRICT_PLAN,
  SIGNUP_ROLES,
  composeGrantedRole,
  gradeBand,
  BAND,
  MAX_SEATS,
  CLASSROOM_BANDS,
  FREE_TRIAL_DAYS,
  TRIAL_STUDENT_CAP,
} from "../config/plans";
import { SUBJECTS } from "../data/subjects";
import {
  PAID_SUBJECTS,
  enrolledCourses,
  optionsForSubject,
  setCourse,
  courseLabel,
  billableCourses,
  priceFor,
  bestPlanFor,
} from "../utils/enrollment";
import { isEmailConfirmed } from "../utils/studentLogin";

export default function ChoosePlan() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seats, setSeats] = useState(1);
  // A MAP, mirroring users/{uid}.courses — not a flat list of tokens.
  //
  // The old array let someone tick Geometry and Algebra 2 together and be
  // billed for both. Keying by subject makes that impossible: a second maths
  // choice overwrites the first because there is nowhere else to put it.
  const [schedule, setSchedule] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  // Classroom plans show one billing period at a time
  const [classBilling, setClassBilling] = useState("monthly");
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }
      await user.reload();
      if (!isEmailConfirmed(user)) {
        navigate("/verify-email", { replace: true });
        return;
      }
      const snap = await getDoc(doc(db, "users", user.uid));
      const data = snap.exists() ? snap.data() : null;
      setProfile(data);
      // Seeded from the schedule they gave at registration, so this page
      // opens showing what they already have rather than an empty form. The
      // ask here is "keep or change", not "start again".
      setSchedule(enrolledCourses(data || {}));
      setLoading(false);
    });
    return () => unsub();
  }, [navigate]);

  const role = profile?.intendedRole || "";
  const grade = profile?.grade || "";
  const state = profile?.state || "";

  // Elementary gets the two all-subject plans it always got. 6-12 gets four:
  // one subject and all subjects, monthly and yearly.
  const plans = useMemo(
    () => plansForRoleAndGrade(role, grade),
    [role, grade]
  );

  const isSecondary = gradeBand(grade) === BAND.SECONDARY;

  // ALL-ACCESS PLANS. Elementary plans (every role) and every educator plan
  // cover all subjects of the grade — there is nothing to pick. Only 6-12
  // students/parents choose classes, priced per course.
  //
  // This used to run every checkout through bestPlanFor(), which builds the
  // plan from the picked classes. With no class picker on screen (elementary)
  // the schedule is empty, so it returned no plan and the page said "Pick at
  // least one subject" — blocking every K-5 checkout and every educator.
  const allAccess = !isSecondary || role === SIGNUP_ROLES.EDUCATOR;

  // One group per PAID subject. "other" is absent on purpose — it's free
  // with every plan and with none, so it has no place on a pricing screen.
  const courseGroups = useMemo(() => {
    if (allAccess || !state || !grade) return [];

    return SUBJECTS.filter((s2) => PAID_SUBJECTS.includes(s2.value)).map(
      (subject) => ({
        subject,
        options: optionsForSubject({ state, grade, subject: subject.value }),
      })
    );
  }, [allAccess, state, grade]);

  const chosenTokens = billableCourses(schedule);

  // What they'll actually be charged, with the cap applied, and which of the
  // two existing Stripe Prices delivers it.
  //
  // Because a student takes at most one maths, one science and one social
  // studies, three is the ceiling — so the "all subjects" price isn't an
  // open-ended upsell, it's precisely the three-subject price. At three,
  // $12.99 beats 3 x $4.99, and bestPlanFor picks it automatically instead of
  // leaving the customer to notice.
  const pricing = useMemo(
    () => priceFor({ role, billing: "monthly", count: chosenTokens.length }),
    [role, chosenTokens.length]
  );

  const toggleCourse = (subject, token) =>
    setSchedule((prev) =>
      prev[subject] === token
        ? setCourse(prev, subject, null)   // clicking the current one clears it
        : setCourse(prev, subject, token)
    );

  const grantedRole = composeGrantedRole({
    state: profile?.state,
    grade: profile?.grade,
    role,
  });

  const startCheckout = async (billing, classroomKey = null) => {
    // The plan is DERIVED, not clicked.
    //
    // The page used to show four plan cards and let the customer pick. But
    // three of the four choices are already determined by the schedule above:
    // whether it's subject-scoped or the bundle depends only on how many
    // subjects are ticked, and picking the wrong one means overpaying. So the
    // only real question left is monthly or yearly, and that's what the two
    // buttons ask. bestPlanFor() answers the rest.
    const chosen = classroomKey
      ? { planKey: classroomKey, courses: [], quantity: 1 }
      : allAccess
      ? { planKey: `${role}_${billing}`, courses: [], quantity: seats }
      : bestPlanFor({ role, billing, courses: schedule });

    if (!chosen.planKey) {
      setError("Pick at least one subject before continuing.");
      return;
    }

    setBusyKey(chosen.planKey);
    setError("");
    try {
      const createCheckoutSession = httpsCallable(functions, "createCheckoutSession");
      const { data } = await createCheckoutSession({
        planKey: chosen.planKey,
        // Seats only apply to the bundle. On a subject-scoped plan the server
        // ignores this and bills by the length of `courses` — see
        // COURSE_QUANTITY_PLANS in functions/stripeSubscriptions.js.
        quantity: classroomKey ? 1 : chosen.courses.length ? chosen.quantity : seats,
        courses: chosen.courses,
        state: profile?.state,
        grade: profile?.grade,
        successUrl: `${window.location.origin}/upgrade-success`,
        cancelUrl: `${window.location.origin}/choose-plan`,
      });
      window.location.href = data.url;
    } catch (err) {
      console.error("Checkout error:", err);
      setError(
        err?.message ||
          "Something went wrong starting checkout. Please try again."
      );
      setBusyKey(null);
    }
  };

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <p style={s.body}>Loading your plan options…</p>
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <h1 style={s.title}>One more detail</h1>
          <p style={s.body}>
            We need your state, grade, and who you're signing up as before we
            can show you the right plan. Your account is already set up — this
            just fills in the gaps.
          </p>
          <button
            type="button"
            style={s.primary}
            onClick={() => navigate("/register")}
          >
            Finish setting up
          </button>
        </div>
      </div>
    );
  }

  if (role === SIGNUP_ROLES.DISTRICT) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <h1 style={s.title}>{DISTRICT_PLAN.title}</h1>
          <p style={s.body}>{DISTRICT_PLAN.blurb}</p>
          <button
            type="button"
            style={s.primary}
            onClick={() => navigate(DISTRICT_PLAN.route)}
          >
            Start a purchase order request
          </button>
        </div>
      </div>
    );
  }

  // Seats are a bundle-only idea. On a subject-scoped purchase the Stripe
  // quantity counts COURSES, not people, so a seat box next to it would be
  // asking a question whose answer gets thrown away — and would read as
  // "3 seats" to someone buying 3 classes for one child.
  const buyingBundle =
    allAccess || priceFor({ role, billing: "monthly", count: chosenTokens.length }).capped;
  const showSeats =
    buyingBundle && (plans.some((p) => p.allowsMultipleSeats) || Boolean(PLANS[`${role}_monthly`]?.allowsMultipleSeats));

  return (
    <div style={s.page}>
      <div style={{ ...s.card, maxWidth: 720 }}>
        <h1 style={s.title}>Choose your plan</h1>
        <p style={s.sub}>
          Step 3 of 3 — {profile?.state} · Grade {profile?.grade} ·{" "}
          {role.charAt(0).toUpperCase() + role.slice(1)}
        </p>

        {error && <div style={s.error}>{error}</div>}

        {role === SIGNUP_ROLES.EDUCATOR && (
          <div style={s.classBox}>
            <div style={s.classHead}>
              <div>
                <h2 style={s.classTitle}>👩‍🏫 Classroom plans — you + your class</h2>
                <p style={s.seatHint}>
                  Every student on your roster gets full access and earns coins. Pick the size of your class.
                </p>
              </div>
              <div style={s.toggle} role="group" aria-label="Billing">
                {["monthly", "yearly"].map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => setClassBilling(b)}
                    style={{ ...s.toggleBtn, ...(classBilling === b ? s.toggleOn : {}) }}
                  >
                    {b === "monthly" ? "Monthly" : "Yearly · 2 months free"}
                  </button>
                ))}
              </div>
            </div>
            <div style={s.plans}>
              {CLASSROOM_BANDS.map((b) => {
                const key = `classroom${b.size}_${classBilling}`;
                return (
                  <div key={key} style={{ ...s.plan, ...(b.popular ? s.planPopular : {}) }}>
                    {b.popular && <span style={s.badge}>Most popular</span>}
                    <h3 style={s.planTitle}>Up to {b.size} students</h3>
                    <div style={s.planPrice}>
                      ${classBilling === "yearly" ? b.yearly : b.monthly} / {classBilling === "yearly" ? "year" : "month"}
                    </div>
                    <p style={s.planBlurb}>{b.blurb}</p>
                    <button
                      type="button"
                      style={{ ...s.primary, opacity: busyKey ? 0.6 : 1 }}
                      disabled={Boolean(busyKey)}
                      onClick={() => startCheckout(classBilling, key)}
                    >
                      {busyKey === key ? "Redirecting…" : "Choose"}
                    </button>
                  </div>
                );
              })}
            </div>
            <h3 style={{ ...s.classTitle, fontSize: 16, marginTop: 22 }}>Just me — Teacher plan</h3>
            <p style={s.seatHint}>
              Your teacher portal only. Students can still join your class: they get the first 3 topics of each subject free,
              or full access with their own Student plan.
            </p>
          </div>
        )}

        {showSeats && (
          <div style={s.seatBox}>
            <label style={s.label} htmlFor="seat-count">
              How many seats?
            </label>
            <input
              id="seat-count"
              type="number"
              min={1}
              max={MAX_SEATS}
              value={seats}
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                setSeats(Number.isNaN(n) ? 1 : Math.min(Math.max(n, 1), MAX_SEATS));
              }}
              style={s.seatInput}
            />
            <p style={s.seatHint}>
              Each seat is one person. You'll assign them after checkout — buying
              them now doesn't require knowing who fills them yet.
            </p>
          </div>
        )}

        {!allAccess && courseGroups.length > 0 && (
          <div style={s.courseBox}>
            <label style={s.label}>Your classes</label>
            <p style={s.seatHint}>
              One class per subject — you can't be in two maths at once.
              Art, music and PE are included free and aren't listed here.
            </p>

            {courseGroups.map(({ subject, options }) => (
              <div key={subject.value} style={s.courseGroup}>
                <div style={s.courseGroupTitle}>
                  <span aria-hidden="true">{subject.icon}</span> {subject.label}
                </div>
                {options.map((opt) => (
                  <label key={opt.token} style={s.courseRow}>
                    <input
                      type="radio"
                      name={`plan-course-${subject.value}`}
                      checked={schedule[subject.value] === opt.token}
                      onChange={() => toggleCourse(subject.value, opt.token)}
                      style={s.courseCheck}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
                <label style={s.courseRow}>
                  <input
                    type="radio"
                    name={`plan-course-${subject.value}`}
                    checked={!schedule[subject.value]}
                    onChange={() => setSchedule((c) => setCourse(c, subject.value, null))}
                    style={s.courseCheck}
                  />
                  <span style={s.courseNone}>Not taking {subject.label}</span>
                </label>
              </div>
            ))}

            <div style={s.courseTotal}>
              {chosenTokens.length === 0 ? (
                <span style={s.courseNone}>No subjects selected yet.</span>
              ) : (
                <>
                  {chosenTokens.map((token) => {
                    const subject = PAID_SUBJECTS.find(
                      (sub) => schedule[sub] === token
                    );
                    return (
                      <div key={token} style={s.courseLine}>
                        {courseLabel({ state, subject, token, grade })}
                      </div>
                    );
                  })}

                  <div style={s.priceLine}>
                    <strong>${pricing.total.toFixed(2)} / month</strong>
                    {/* The saving is stated, not left to be discovered.
                        Three subjects prices at $14.97 and bills at $12.99;
                        a customer who works that out afterwards feels lucky,
                        one who's told beforehand feels looked after. */}
                    {pricing.capped && (
                      <span style={s.cappedNote}>
                        {" "}
                        — capped. All three subjects would be $
                        {pricing.unitTotal.toFixed(2)} separately, so you're on
                        the All Subjects plan instead.
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div style={s.plans}>
          {["monthly", "yearly"].map((billing) => (
            <div key={billing} style={s.plan}>
              <h3 style={s.planTitle}>
                {billing === "yearly" ? "Yearly" : "Monthly"}
              </h3>
              <div style={s.planPrice}>
                {!allAccess
                  ? `$${priceFor({ role, billing, count: chosenTokens.length }).total.toFixed(2)} / ${billing === "yearly" ? "year" : "month"}`
                  : (plans.find((p) => p.billing === billing) || PLANS[`${role}_${billing}`])?.priceLabel || ""}
              </div>
              {billing === "yearly" && (
                <div style={s.savings}>Two months free vs. monthly</div>
              )}
              <button
                type="button"
                style={{ ...s.primary, opacity: busyKey ? 0.6 : 1 }}
                disabled={Boolean(busyKey)}
                onClick={() => startCheckout(billing)}
              >
                {busyKey ? "Redirecting…" : `Continue ${billing}`}
              </button>
            </div>
          ))}
        </div>

        {grantedRole && (
          <p style={s.preview}>
            After payment your account unlocks: <code>{grantedRole}</code>
          </p>
        )}

        <p style={s.trialNote}>
          Every new account starts with a {FREE_TRIAL_DAYS}-day free trial with everything except Request Custom
          Material{role === SIGNUP_ROLES.EDUCATOR ? ` (and up to ${TRIAL_STUDENT_CAP} students in your class)` : ""}.
          After the trial, accounts without a plan keep their dashboard, avatar and items, with the first 3 topics of
          each subject.
        </p>

        {role === SIGNUP_ROLES.EDUCATOR && (
          <p style={s.foot}>
            <strong>Whole school or district?</strong> We price by classroom, invoiced by purchase order.{" "}
            <button type="button" style={s.inlineLink} onClick={() => navigate("/purchase-order")}>
              Get a school / district quote
            </button>
          </p>
        )}

        <p style={s.foot}>
          Paying by school purchase order instead?{" "}
          <button
            type="button"
            style={s.inlineLink}
            onClick={() => navigate("/purchase-order")}
          >
            Request a PO
          </button>
        </p>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  card: {
    background: "#fff",
    maxWidth: 520,
    width: "100%",
    borderRadius: 12,
    padding: 32,
    boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
  },
  title: { margin: 0, fontSize: "clamp(1.6rem, 4vw, 2rem)", color: "#111" },
  sub: { color: "#555", fontSize: 14, marginTop: 6, marginBottom: 20 },
  body: { color: "#444", fontSize: 15, lineHeight: 1.6 },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#374151" },
  seatBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: 16,
    marginBottom: 20,
  },
  seatInput: {
    width: 120,
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    fontSize: 15,
    marginTop: 8,
  },
  seatHint: { fontSize: 12, color: "#6b7280", marginTop: 8, marginBottom: 0 },

  courseBox: {
    background: "#faf5ff",
    border: "1px solid #e9d5ff",
    borderRadius: 10,
    padding: 16,
    marginBottom: 20,
    textAlign: "left",
  },
  courseGroup: { marginTop: 14 },
  courseGroupTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#4c1d95",
    marginBottom: 6,
  },
  courseRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    fontSize: 14,
    color: "#374151",
    lineHeight: 1.45,
    padding: "3px 0",
    cursor: "pointer",
  },
  // Sized explicitly. A bare `.form-wrapper input` rule elsewhere in this app
  // has already stretched checkboxes to full width once — scoping the size
  // here is cheaper than finding out again in production.
  courseCheck: {
    width: 16,
    height: 16,
    minWidth: 16,
    marginTop: 3,
    accentColor: "#7f00ff",
  },
  courseTotal: {
    marginTop: 16,
    marginBottom: 0,
    paddingTop: 12,
    borderTop: "1px solid #e9d5ff",
    fontSize: 14,
    color: "#374151",
  },
  courseNone: { color: "#6b7280" },
  courseLine: { fontSize: 14, color: "#374151", padding: "2px 0" },
  priceLine: { fontSize: 15, color: "#111827", marginTop: 10, lineHeight: 1.5 },
  cappedNote: { color: "#6d28d9", fontWeight: 600, fontSize: 13 },
  plans: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: 16,
  },
  plan: {
    border: "2px solid #e5e7eb",
    borderRadius: 12,
    padding: 20,
    display: "flex",
    flexDirection: "column",
  },
  planTitle: { margin: 0, fontSize: 17, color: "#111" },
  planPrice: {
    fontSize: "clamp(1.1rem, 3vw, 1.35rem)",
    fontWeight: 700,
    marginTop: 8,
    background: "linear-gradient(90deg, #e100ff, #7f00ff)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  savings: { fontSize: 12, color: "#16a34a", fontWeight: 600, marginTop: 4 },
  planBlurb: { fontSize: 13, color: "#555", lineHeight: 1.5, flexGrow: 1, marginTop: 10 },
  primary: {
    width: "100%",
    marginTop: 14,
    padding: "11px 16px",
    borderRadius: 8,
    border: "none",
    background: "linear-gradient(90deg, #e100ff, #7f00ff)",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },
  preview: { fontSize: 13, color: "#374151", marginTop: 18 },
  classBox: {
    background: "#f5f3ff",
    border: "1px solid #ddd6fe",
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    textAlign: "left",
  },
  classHead: { display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 },
  classTitle: { margin: 0, fontSize: 18, color: "#2e1065" },
  toggle: { display: "inline-flex", background: "#fff", border: "1px solid #ddd6fe", borderRadius: 999, padding: 3 },
  toggleBtn: { border: 0, background: "none", borderRadius: 999, padding: "6px 12px", fontSize: 13, fontWeight: 700, color: "#5b21b6", cursor: "pointer" },
  toggleOn: { background: "#7f00ff", color: "#fff" },
  planPopular: { borderColor: "#7f00ff", position: "relative", background: "#fff" },
  badge: { alignSelf: "flex-start", background: "#7f00ff", color: "#fff", borderRadius: 999, padding: "2px 10px", fontSize: 11, fontWeight: 800, marginBottom: 6 },
  trialNote: { fontSize: 13, color: "#4b5563", marginTop: 18, lineHeight: 1.5, background: "#f8fafc", borderRadius: 10, padding: "10px 12px" },
  foot: { fontSize: 14, marginTop: 10, color: "#555" },
  inlineLink: {
    background: "none",
    border: "none",
    color: "#7f00ff",
    fontSize: 14,
    cursor: "pointer",
    textDecoration: "underline",
    padding: 0,
  },
  error: {
    background: "#fee2e2",
    border: "1px solid #fca5a5",
    color: "#991b1b",
    padding: "10px 12px",
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 16,
  },
};
